import { EMPTY_VALUE_LABEL } from '@sales-portal/constants';
import { formatCurrency } from '@/utils/money';
import type { GetOrderEntryItemDetailQuery } from '@/types';

/**
 * Sibling variant pack sharing the same item_id, with display strings
 * pre-computed for the Details tab's variant-pack cards.
 */
export interface RelatedVariantPack {
  availabilityStatus: string | null;
  currencyCode: string;
  externalItemId: string | null;
  packDisplayString: string;
  priceText: string;
  subtitle: string;
  unitPriceInMicros: number | null;
  variantPackId: string;
}

type GqlRelatedVariantPack =
  GetOrderEntryItemDetailQuery['getOrderEntryItemDetail']['related_variant_packs'][number];

const buildSubtitle = (
  packDisplayString: string,
  externalItemId: string | null,
): string =>
  externalItemId
    ? `${packDisplayString} • ${externalItemId}`
    : packDisplayString;

const buildPriceText = (
  unitPriceInMicros: number | null,
  currencyCode: string,
): string =>
  unitPriceInMicros === null
    ? EMPTY_VALUE_LABEL
    : formatCurrency(unitPriceInMicros, currencyCode);

/**
 * Maps the GraphQL related variant pack list into the panel's
 * {@link RelatedVariantPack} shape, precomputing the subtitle (pack +
 * external item id) and price text (formatted currency or the empty-value
 * placeholder) so leaf components stay presentational.
 *
 * @param packs - Raw `related_variant_packs` from `getOrderEntryItemDetail`.
 * @returns Mapped variant pack rows ready to render.
 */
export const buildRelatedVariantPacks = (
  packs: readonly GqlRelatedVariantPack[],
): RelatedVariantPack[] =>
  packs.map((vp) => ({
    availabilityStatus: vp.availability_status ?? null,
    currencyCode: vp.currency_code,
    externalItemId: vp.external_item_id ?? null,
    packDisplayString: vp.pack_display_string,
    priceText: buildPriceText(
      vp.unit_price_in_micros ?? null,
      vp.currency_code,
    ),
    subtitle: buildSubtitle(
      vp.pack_display_string,
      vp.external_item_id ?? null,
    ),
    unitPriceInMicros: vp.unit_price_in_micros ?? null,
    variantPackId: vp.variant_pack_id,
  }));
